import { useState, useMemo, useEffect } from "react"
import { X, ExternalLink, MapPin, ArrowRight, ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from "lucide-react"
import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { Badge } from "@/components/ui/badge"
import { classifyAgeGroup } from "./FilterList"
import type { ParasiteRatePoint } from "@/services/parasiteRateService"

// ---------------------------------------------------------------------------
// Field Label Map
// ---------------------------------------------------------------------------

/**
 * Map of field keys to human-readable display labels. Every column from the
 * ParasiteRatePoint interface is listed here. Override individual entries as
 * needed — the auto-formatter (split on _ and capitalise) is only used as a
 * fallback for any future fields not yet in this map.
 */
const FIELD_LABEL_OVERRIDES: Record<string, string> = {
  country: "Country",
  country_id: "Country ID",
  cluster_id: "Cluster ID",
  site_name: "Site Name",
  dhs_survey_num: "DHS Survey Number",
  dhs_survey_id: "DHS Survey ID",
  dhs_id: "DHS ID",
  latitude: "Latitude",
  longitude: "Longitude",
  rural_urban_type: "Rural/Urban Type",
  extracted_admin_1_name: "Admin 1 Name",
  admin1_dhs: "DHS Admin 1 Code",
  dhs_region_code: "DHS Region Code",
  dhs_region_name: "DHS Region Name",
  lat_long_source: "Lat/Long Source",
  area_type: "Area Type",
  area_size: "Area Size",
  is_school: "Is School",
  is_excluded: "Is Excluded",
  exclusion_reason: "Exclusion Reason",
  survey_notes: "Survey Notes",
  site_notes: "Site Notes",
  pcr_type: "PCR Type",
  rdt_type: "RDT Type",
  micro_type: "Microscopy Type",
  method: "Method",
  household_sample_weight: "Household Sample Weight",
  month_start: "Month Start",
  month_end: "Month End",
  day_start: "Day Start",
  day_end: "Day End",
  year_start: "Year Start",
  year_end: "Year End",
  lower_age_months_rounded: "Lower Age (Months)",
  upper_age_months_rounded: "Upper Age (Months)",
  examined: "Examined",
  any_positive_count: "Any Positive Count",
  any_positive_rate: "Any Positive Rate",
  pf_positive_count: "Pf Positive Count",
  pf_positive_rate: "Pf Positive Rate",
  computed_total_pf_positive_count: "Computed Total Pf Positive Count",
  computed_total_pf_positive_rate: "Computed Total Pf Positive Rate",
  pv_positive_count: "Pv Positive Count",
  pv_positive_rate: "Pv Positive Rate",
  computed_total_pv_positive_count: "Computed Total Pv Positive Count",
  computed_total_pv_positive_rate: "Computed Total Pv Positive Rate",
  pk_positive_count: "Pk Positive Count",
  pk_positive_rate: "Pk Positive Rate",
  computed_total_pk_positive_count: "Computed Total Pk Positive Count",
  computed_total_pk_positive_rate: "Computed Total Pk Positive Rate",
  po_positive_count: "Po Positive Count",
  po_positive_rate: "Po Positive Rate",
  computed_total_po_positive_count: "Computed Total Po Positive Count",
  computed_total_po_positive_rate: "Computed Total Po Positive Rate",
  pm_positive_count: "Pm Positive Count",
  pm_positive_rate: "Pm Positive Rate",
  computed_total_pm_positive_count: "Computed Total Pm Positive Count",
  computed_total_pm_positive_rate: "Computed Total Pm Positive Rate",
  pan_positive_count: "Pan Positive Count",
  pan_positive_rate: "Pan Positive Rate",
  pf_pv_positive_count: "Pf/Pv Positive Count",
  pf_pv_positive_rate: "Pf/Pv Positive Rate",
  pf_po_positive_count: "Pf/Po Positive Count",
  pf_po_positive_rate: "Pf/Po Positive Rate",
  pf_pm_positive_count: "Pf/Pm Positive Count",
  pf_pm_positive_rate: "Pf/Pm Positive Rate",
  pv_pm_positive_count: "Pv/Pm Positive Count",
  pv_pm_positive_rate: "Pv/Pm Positive Rate",
  pv_po_positive_count: "Pv/Po Positive Count",
  pv_po_positive_rate: "Pv/Po Positive Rate",
  po_pm_positive_count: "Po/Pm Positive Count",
  po_pm_positive_rate: "Po/Pm Positive Rate",
  pk_pf_positive_count: "Pk/Pf Positive Count",
  pk_pf_positive_rate: "Pk/Pf Positive Rate",
  pk_pv_positive_count: "Pk/Pv Positive Count",
  pk_pv_positive_rate: "Pk/Pv Positive Rate",
  pk_pm_positive_count: "Pk/Pm Positive Count",
  pk_pm_positive_rate: "Pk/Pm Positive Rate",
  pk_po_positive_count: "Pk/Po Positive Count",
  pk_po_positive_rate: "Pk/Po Positive Rate",
  pf_pv_po_positive_count: "Pf/Pv/Po Positive Count",
  pf_pv_po_positive_rate: "Pf/Pv/Po Positive Rate",
  pf_pv_pm_positive_count: "Pf/Pv/Pm Positive Count",
  pf_pv_pm_positive_rate: "Pf/Pv/Pm Positive Rate",
  pf_po_pm_positive_count: "Pf/Po/Pm Positive Count",
  pf_po_pm_positive_rate: "Pf/Po/Pm Positive Rate",
  pv_po_pm_positive_count: "Pv/Po/Pm Positive Count",
  pv_po_pm_positive_rate: "Pv/Po/Pm Positive Rate",
  pf_pv_pk_positive_count: "Pf/Pv/Pk Positive Count",
  pf_pv_pk_positive_rate: "Pf/Pv/Pk Positive Rate",
  pf_po_pk_positive_count: "Pf/Po/Pk Positive Count",
  pf_po_pk_positive_rate: "Pf/Po/Pk Positive Rate",
  pf_pm_pk_positive_count: "Pf/Pm/Pk Positive Count",
  pf_pm_pk_positive_rate: "Pf/Pm/Pk Positive Rate",
  pv_po_pk_positive_count: "Pv/Po/Pk Positive Count",
  pv_po_pk_positive_rate: "Pv/Po/Pk Positive Rate",
  pv_pm_pk_positive_count: "Pv/Pm/Pk Positive Count",
  pv_pm_pk_positive_rate: "Pv/Pm/Pk Positive Rate",
  po_pm_pk_positive_count: "Po/Pm/Pk Positive Count",
  po_pm_pk_positive_rate: "Po/Pm/Pk Positive Rate",
  pf_pv_po_pm_positive_count: "Pf/Pv/Po/Pm Positive Count",
  pf_pv_po_pm_positive_rate: "Pf/Pv/Po/Pm Positive Rate",
  pf_pan_positive_count: "Pf/Pan Positive Count",
  pf_pan_positive_rate: "Pf/Pan Positive Rate",
  pv_pan_positive_count: "Pv/Pan Positive Count",
  pv_pan_positive_rate: "Pv/Pan Positive Rate",
  pf_pv_pan_positive_count: "Pf/Pv/Pan Positive Count",
  pf_pv_pan_positive_rate: "Pf/Pv/Pan Positive Rate",
  map_survey_id: "MAP Survey ID",
  source_id1: "Source ID 1",
  lit_review_id: "Literature Review ID",
  extracted_admin_2_name: "Admin 2 Name",
  extracted_admin_3_name: "Admin 3 Name",
  source_id2: "Source ID 2",
  source_id3: "Source ID 3",
  validation_error: "Validation Error",
}

/**
 * Get the display label for a field key. Uses the override map if an entry
 * exists, otherwise splits on underscores and capitalises each word.
 *
 * @param key - The raw field key from ParasiteRatePoint (e.g. "year_start").
 * @returns A human-readable label (e.g. "Year Start").
 */
function getFieldLabel(key: string): string {
  if (FIELD_LABEL_OVERRIDES[key]) {
    return FIELD_LABEL_OVERRIDES[key]
  }

  // Default: split on _ and capitalise each word
  return key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
}

/**
 * @param latitude - The point latitude in decimal degrees, or null.
 * @param longitude - The point longitude in decimal degrees, or null.
 * @returns A string such as "-1.2921, 36.8219", or "Coordinates unavailable".
 */
function formatCoordinates({
  latitude,
  longitude,
}: {
  latitude: number | null
  longitude: number | null
}): string {
  if (latitude == null || longitude == null) {
    return "Coordinates unavailable"
  }

  return `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`
}

/**
 * Normalise the raw rural_urban_type value into a display label for the top
 * portion of the card. The source data is inconsistent (e.g. "Urban", "u",
 * "PERI_URBAN", "R", "1", "UNKNOWN"), so we map only values we can confidently
 * classify and hide everything else rather than guessing.
 *
 * @param rawType - The raw rural_urban_type field from the point, or null.
 * @returns "URBAN", "RURAL", or null when the value cannot be classified.
 */
function normaliseRuralUrbanLabel(rawType: string | null): "URBAN" | "RURAL" | null {
  if (rawType == null) {
    return null
  }

  const normalised: string = rawType.trim().toLowerCase()

  // Substring checks first so multi-word values like "peri_urban" are captured.
  if (normalised.includes("urban")) {
    return "URBAN"
  }
  if (normalised.includes("rural")) {
    return "RURAL"
  }

  // Single-letter shorthands.
  if (normalised === "u") {
    return "URBAN"
  }
  if (normalised === "r") {
    return "RURAL"
  }

  // Unclassifiable - show nothing.
  return null
}

/**
 * Interpret the raw is_school field as a boolean
 * @param rawIsSchool - The raw is_school field from the point (boolean at
 *   runtime despite the string type), or null.
 * @returns true when the value indicates a school site, false otherwise.
 */
function isSchoolSite(rawIsSchool: string | null): boolean {
  if (rawIsSchool == null) {
    return false
  }

  if (typeof rawIsSchool === "boolean") {
    return rawIsSchool
  }

  // Defensive fallback in case the value ever arrives as a string token.
  const normalised: string = String(rawIsSchool).trim().toLowerCase()
  return normalised === "true"
}

/**
 * Derive the age-band pill label from the lower and upper age bounds (in
 * months).
 *
 * @param lowerAgeMonths - The rounded lower age bound in months, or null.
 * @param upperAgeMonths - The rounded upper age bound in months, or null.
 * @returns "Under 5", "Over 5", or null when no single band applies.
 */
function getAgeBandLabel(
  lowerAgeMonths: number | null,
  upperAgeMonths: number | null,
): "Under 5" | "Over 5" | null {
  const ageGroup = classifyAgeGroup(lowerAgeMonths, upperAgeMonths)
  return ageGroup === "Under 5" || ageGroup === "Over 5" ? ageGroup : null
}

/**
 * Interpret the raw is_excluded field as a boolean.
 *
 * @param rawIsExcluded - The raw is_excluded field from the point (boolean at
 *   runtime despite the string type), or null.
 * @returns true when the cluster is flagged as excluded, false otherwise.
 */
function isExcludedSite(rawIsExcluded: string | null): boolean {
  if (rawIsExcluded == null) {
    return false
  }

  // Runtime value is a genuine boolean coming from the dataset.
  if (typeof rawIsExcluded === "boolean") {
    return rawIsExcluded
  }

  // Defensive fallback in case the value ever arrives as a string token.
  const normalised: string = String(rawIsExcluded).trim().toLowerCase()
  return normalised === "true"
}

// ---------------------------------------------------------------------------
// Positive-count species options
// ---------------------------------------------------------------------------
/**
 * Ordered list of the positive-count field keys. This is the single source of
 * truth for both the display order in the strip and the PositiveCountKey type
 * below. The `satisfies` clause guarantees every entry is a real field on
 * ParasiteRatePoint, so a typo fails to compile.
 */
const POSITIVE_COUNT_KEYS = [
  "any_positive_count",
  "pf_positive_count",
  "pv_positive_count",
  "pk_positive_count",
  "po_positive_count",
  "pm_positive_count",
  "pan_positive_count",
  "pf_pv_positive_count",
  "pf_po_positive_count",
  "pf_pm_positive_count",
  "pv_pm_positive_count",
  "pv_po_positive_count",
  "po_pm_positive_count",
  "pk_pf_positive_count",
  "pk_pv_positive_count",
  "pk_pm_positive_count",
  "pk_po_positive_count",
  "pf_pv_po_positive_count",
  "pf_pv_pm_positive_count",
  "pf_po_pm_positive_count",
  "pv_po_pm_positive_count",
  "pf_pv_pk_positive_count",
  "pf_po_pk_positive_count",
  "pf_pm_pk_positive_count",
  "pv_po_pk_positive_count",
  "pv_pm_pk_positive_count",
  "po_pm_pk_positive_count",
  "pf_pv_po_pm_positive_count",
  "pf_pan_positive_count",
  "pv_pan_positive_count",
  "pf_pv_pan_positive_count",
] as const satisfies readonly (keyof ParasiteRatePoint)[]

/** A positive-count field key, derived from the POSITIVE_COUNT_KEYS array. */
type PositiveCountKey = (typeof POSITIVE_COUNT_KEYS)[number]

/**
 * Derive the short strip label for a positive-count field key. Strips the
 * trailing "_positive_count" suffix, then capitalises the first letter of each
 * remaining underscore-separated species token and concatenates them with no
 * separator (e.g. "pf_pv_positive_count" -> "PfPv", "any_positive_count" ->
 * "Any").
 *
 * @param key - The positive-count field key from ParasiteRatePoint.
 * @returns The compact strip label for the option.
 */
function getPositiveCountLabel(key: PositiveCountKey): string {
  const withoutSuffix: string = key.replace(/_positive_count$/, "")
  return withoutSuffix
    .split("_")
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
    .join("")
}

/**
 * Map from a base per-species positive-count key to its `computed_total_*`
 * variant, for the five single-species that have one (Pf, Pv, Pk, Po, Pm). The
 * base field is the mono-infection count; the computed_total field is the
 * "mixed" total that also includes co-infections. Keys not present here have no
 * computed variant.
 */
const COMPUTED_TOTAL_COUNT_KEY: Partial<Record<PositiveCountKey, keyof ParasiteRatePoint>> = {
  pf_positive_count: "computed_total_pf_positive_count",
  pv_positive_count: "computed_total_pv_positive_count",
  pk_positive_count: "computed_total_pk_positive_count",
  po_positive_count: "computed_total_po_positive_count",
  pm_positive_count: "computed_total_pm_positive_count",
}

/**
 * Derive the matching positive-rate key for any positive-count key on the point
 * (works for both base and computed_total keys by swapping the suffix).
 *
 * @param countKey - A positive-count field key.
 * @returns The corresponding positive-rate field key.
 */
function getRateKeyForCountKey(countKey: keyof ParasiteRatePoint): keyof ParasiteRatePoint {
  return String(countKey).replace(/_count$/, "_rate") as keyof ParasiteRatePoint
}

/**
 * Format a numeric value for display, or return a visible placeholder when it
 * is missing. We avoid silently substituting 0 so absent data is obvious.
 *
 * @param value - The numeric value from the point, or null.
 * @returns The stringified number, or an em dash when null.
 */
function formatNumericValue(value: number | null): string {
  return value == null ? "—" : String(value)
}

/**
 * Three-letter month abbreviations indexed by 1-based month number (index 0 is
 * a placeholder so that MONTH_ABBREVIATIONS[1] === "Jan").
 */
const MONTH_ABBREVIATIONS: readonly string[] = [
  "",
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
]

/**
 * Format a month/year pair into a display string.
 * @param month - The 1-based month number from the point, or null.
 * @param year - The year from the point, or null.
 * @returns The formatted "MMM YYYY" / "YYYY" string, or null when no year.
 */
function formatMonthYear(month: number | null, year: number | null): string | null {
  if (year == null) {
    return null
  }

  // Only treat the month as valid if it is an integer in the 1-12 range.
  const isValidMonth: boolean = month != null && Number.isInteger(month) && month >= 1 && month <= 12

  if (isValidMonth) {
    return `${MONTH_ABBREVIATIONS[month as number]} ${year}`
  }

  return String(year)
}

/**
 * Format a rate value by rounding to at most four decimal places
 *
 * @param value - The rate value from the point, or null.
 * @returns The rounded rate as a string, or an em dash when null.
 */
function formatRateValue(value: number | null): string {
  if (value == null) {
    return "—"
  }

  // Round to 4 decimal places; Number() drops any trailing zeros from toFixed.
  const rounded: number = Number(value.toFixed(4))
  return String(rounded)
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface PointDetailCardProps {
  /** The selected parasite rate data point to display. */
  point: ParasiteRatePoint
  /** Callback to close/dismiss the card. */
  onClose: () => void
  /**
   * Total number of points sharing this point's map location (including this
   * point). When greater than 1, the header shows pager controls so the user
   * can step through points that would otherwise be hidden beneath this one.
   */
  coLocatedCount: number
  /**
   * Zero-based index of this point within the co-located group, used to render
   * the human-friendly "n / total" position indicator (n = index + 1).
   */
  coLocatedIndex: number
  /** Select the previous co-located point (wraps from the first to the last). */
  onNavigatePrev: () => void
  /** Select the next co-located point (wraps from the last to the first). */
  onNavigateNext: () => void
}

/**
 * PointDetailCard renders a fixed-height scrollable overlay card on the right side of the
 * map showing all fields for a selected ParasiteRatePoint.
 *
 * Each field is displayed as a label/value pair with
 * human-readable labels derived from FIELD_LABEL_OVERRIDES or auto-formatting.
 * Null values are shown as "null" in muted text.
 *
 * @param props - The selected point and close handler.
 * @returns A positioned card element overlaid on the map.
 */
export default function PointDetailCard({
  point,
  onClose,
  coLocatedCount,
  coLocatedIndex,
  onNavigatePrev,
  onNavigateNext,
}: PointDetailCardProps): React.JSX.Element {
  // Show the pager only when more than one point shares this location. The
  // human-facing position is 1-based (index 0 -> "1").
  const hasCoLocatedSiblings: boolean = coLocatedCount > 1
  const displayPosition: number = coLocatedIndex + 1
  // Whether the expandable "See more" section (all remaining fields) is open.
  const [isExpanded, setIsExpanded] = useState<boolean>(false)

  // Human-readable fallbacks. We deliberately surface a visible placeholder
  // rather than silently defaulting so missing data is obvious to the user.
  const countryName: string = point.country ?? "Unknown country"
  const ruralUrbanLabel: "URBAN" | "RURAL" | null = normaliseRuralUrbanLabel(point.rural_urban_type)
  const isSchool: boolean = isSchoolSite(point.is_school)
  const isExcluded: boolean = isExcludedSite(point.is_excluded)
  const ageBandLabel: "Under 5" | "Over 5" | null = getAgeBandLabel(
    point.lower_age_months_rounded,
    point.upper_age_months_rounded,
  )
  const coordinatesLabel: string = formatCoordinates({
    latitude: point.latitude,
    longitude: point.longitude,
  })

  // The subset of positive-count options this cluster actually has data for.
  // An option is available when its base (mono) count is non-null OR, for the
  // single-species that have one, its computed_total (mixed) count is non-null.
  // Recomputed only when the point changes.
  const availableCountOptions: Array<{ key: PositiveCountKey; label: string }> = useMemo(() => {
    return POSITIVE_COUNT_KEYS.filter((key) => {
      const computedKey = COMPUTED_TOTAL_COUNT_KEY[key]
      const hasMono = point[key] != null
      const hasComputed = computedKey != null && point[computedKey] != null
      return hasMono || hasComputed
    }).map((key) => ({
      key,
      label: getPositiveCountLabel(key),
    }))
  }, [point])

  // The currently selected positive-count option. Defaults to the first
  // available option.
  const defaultSelectedKey: PositiveCountKey | null =
    availableCountOptions.length > 0 ? availableCountOptions[0].key : null
  const [selectedCountKey, setSelectedCountKey] = useState<PositiveCountKey | null>(defaultSelectedKey)

  // When the point changes, the previously selected option may no longer be
  // available. Reset the selection back to the default in that case so the
  // strip and breakdown always reflect a valid, present option.
  useEffect(() => {
    const isSelectionAvailable =
      selectedCountKey != null && availableCountOptions.some((option) => option.key === selectedCountKey)
    if (!isSelectionAvailable) {
      setSelectedCountKey(defaultSelectedKey)
    }
  }, [availableCountOptions, defaultSelectedKey, selectedCountKey])

  // The count / examined = rate breakdown line(s) for the currently selected
  // option.
  const activeCountLines: Array<{ label: string | null; count: number | null; rate: number | null }> = useMemo(() => {
    if (selectedCountKey == null) {
      return []
    }

    const computedKey = COMPUTED_TOTAL_COUNT_KEY[selectedCountKey]

    // Species with a computed_total variant: show Mono and/or Mixed lines.
    if (computedKey != null) {
      const lines: Array<{ label: string | null; count: number | null; rate: number | null }> = []

      const monoCount = point[selectedCountKey] as number | null
      if (monoCount != null) {
        lines.push({
          label: "Mono",
          count: monoCount,
          rate: point[getRateKeyForCountKey(selectedCountKey)] as number | null,
        })
      }

      const mixedCount = point[computedKey] as number | null
      if (mixedCount != null) {
        lines.push({
          label: "Mono + Mixed",
          count: mixedCount,
          rate: point[getRateKeyForCountKey(computedKey)] as number | null,
        })
      }

      return lines
    }

    // Species without a computed variant: a single, unlabelled line.
    return [
      {
        label: null,
        count: point[selectedCountKey] as number | null,
        rate: point[getRateKeyForCountKey(selectedCountKey)] as number | null,
      },
    ]
  }, [selectedCountKey, point])

  // Survey start/end labels for the bottom portion. Null when no year exists.
  const startLabel: string | null = formatMonthYear(point.month_start, point.year_start)
  const endLabel: string | null = formatMonthYear(point.month_end, point.year_end)

  return (
    <Collapsible
      defaultOpen
      className="group/card w-80 max-h-[45vh] min-h-0 overflow-hidden rounded-xl border border-border/60 bg-background/80 shadow-xl ring-1 ring-black/5 backdrop-blur-md flex flex-col"
    >
      <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-border/60 shrink-0 h-[34px]">
        <CollapsibleTrigger className="group flex flex-1 items-center gap-1.5 text-left text-[11px] font-semibold uppercase tracking-wider text-foreground/80 transition-colors hover:text-foreground">
          <MapPin className="size-3.5 mb-0.5 text-muted-foreground" aria-hidden="true" />
          Cluster Details
          <ChevronRight className="size-3.5 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-90" />
        </CollapsibleTrigger>

        <div className="flex items-center gap-1">
          {/* Co-located pager: only shown when multiple points stack at this
              exact location. Lets the user step through points that deck.gl
              would otherwise bury beneath the topmost one. */}
          {hasCoLocatedSiblings && (
            <div
              className="flex items-center gap-0.5"
              role="group"
              aria-label="Points at this location"
            >
              <Button
                variant="ghost"
                size="icon"
                className="h-4 w-4 shrink-0"
                onClick={onNavigatePrev}
                aria-label="Previous point at this location"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span
                className="min-w-[2.5rem] text-center text-xs tabular-nums text-muted-foreground"
                aria-live="polite"
              >
                {displayPosition} / {coLocatedCount}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-4 w-4 shrink-0"
                onClick={onNavigateNext}
                aria-label="Next point at this location"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}

          <Button
            variant="ghost"
            size="icon"
            className="h-4 w-4 shrink-0 "
            onClick={onClose}
            aria-label="Close detail card"
          >
            <X className="" />
          </Button>
        </div>
      </div>

      <CollapsibleContent className="flex min-h-0 flex-col">
      {/* Scrollable body: either the visual top/middle/bottom portions, or the
          full raw field list when "See more" is expanded. Sizes to content and
          only scrolls once the card reaches its max height. */}
      <div className="min-h-0 overflow-y-auto">
        {!isExpanded && (
          <>
        {/* ---------------------------------------------------------------
            TOP PORTION
            Country name + rural/urban type, and lat/lon with a location icon.
            --------------------------------------------------------------- */}
        <section aria-label="Cluster location summary" className="px-4 py-3 border-b border-border/60">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h4 className="text-sm font-semibold leading-none">{countryName}</h4>
            {ruralUrbanLabel != null && (
              <Badge variant="secondary" className="text-[10px] tracking-wide">
                {ruralUrbanLabel}
              </Badge>
            )}
            {isSchool && (
              <Badge variant="secondary" className="text-[10px] tracking-wide">
                SCHOOL
              </Badge>
            )}
            {isExcluded && (
              <Badge variant="destructive" className="text-[10px] tracking-wide">
                EXCLUDED
              </Badge>
            )}
            {ageBandLabel != null && (
              <Badge variant="secondary" className="text-[10px] tracking-wide">
                {ageBandLabel.toUpperCase()}
              </Badge>
            )}
          </div>
          <div className="mt-1.5 flex items-center gap-1 text-xs text-muted-foreground">
            <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>{coordinatesLabel}</span>
          </div>
        </section>

        {/* ---------------------------------------------------------------
            MIDDLE PORTION
            Single-select strip of the positive-count species this cluster has
            (non-null values only).
            --------------------------------------------------------------- */}
        {availableCountOptions.length > 0 && (
          <section aria-label="Positive count species" className="px-4 py-3 border-b border-border/60">
            <div
              role="radiogroup"
              aria-label="Select a positive count species"
              className="flex flex-wrap justify-center gap-y-2"
            >
              {availableCountOptions.map((option, index) => {
                const isSelected: boolean = option.key === selectedCountKey
                const isFirst: boolean = index === 0
                return (
                  <button
                    key={option.key}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    onClick={() => setSelectedCountKey(option.key)}
                    className={[
                      "px-2 py-0.5 text-[10px] font-medium border border-input transition-colors",
                      // Collapse the shared border between adjacent buttons.
                      isFirst ? "rounded-l-md" : "-ml-px",
                      index === availableCountOptions.length - 1 ? "rounded-r-md" : "",
                      isSelected
                        ? "bg-primary text-primary-foreground border-primary z-10"
                        : "bg-background text-foreground hover:bg-muted",
                    ].join(" ")}
                  >
                    {option.label}
                  </button>
                )
              })}
            </div>

            {/* Method
                count / examined = rate breakdown line(s) for the selection.
                Computed-variant species show separate Mono / Mixed lines. */}
            {activeCountLines.length > 0 && (
              <div className="mt-2">
                {point.method != null && (
                  <p className="text-left text-xs text-muted-foreground">{point.method}</p>
                )}
                {activeCountLines.map((line) => (
                  <p key={line.label ?? "single"} className="text-center text-base text-muted-foreground">
                    {line.label != null && (
                      // inline-block + fixed width + right-aligned text so that
                      // the "Mono" and "Mono + Mixed" labels end at the same
                      // x-position (right-aligned with each other) across lines.
                      <span className="mr-1 inline-block w-[72px] text-right text-[10px]">{line.label}</span>
                    )}
                    <span className="text-base font-semibold text-foreground">{formatNumericValue(line.count)}</span>
                    {" / "}
                    <span className="text-base font-semibold text-foreground">{formatNumericValue(point.examined)}</span>
                    {" = "}
                    <span className="text-base font-semibold text-foreground">{formatRateValue(line.rate)}</span>
                  </p>
                ))}
              </div>
            )}
          </section>
        )}

        {/* ---------------------------------------------------------------
            BOTTOM PORTION
            Survey period (start -> end dates), the linked MAP Survey ID (only
            when present), and the always-visible Source ID.
            --------------------------------------------------------------- */}
        <section aria-label="Survey details" className="px-4 py-3">
            {(startLabel != null || endLabel != null) && (
              <div className="flex items-center gap-2 text-xs">
                <span className="font-medium text-foreground whitespace-nowrap">
                  {startLabel ?? "—"}
                </span>
                {/* Arrow pointing from the start date to the end date: a line
                    that fills the space, capped with an arrowhead. */}
                <span className="flex flex-1 items-center" aria-hidden="true">
                  <span className="h-px flex-1 bg-border" />
                  <ArrowRight className="h-3.5 w-3.5 shrink-0 -ml-1 text-border" />
                </span>
                <span className="font-medium text-foreground whitespace-nowrap">
                  {endLabel ?? "—"}
                </span>
              </div>
            )}

            {/* MAP Survey ID with a link to the survey catalogue */}
            {point.map_survey_id != null && (
              <div className="mt-2 flex items-center gap-1 text-xs">
                <span className="text-muted-foreground">Survey:</span>
                <Link
                  to={`/survey-catalogue/${String(point.map_survey_id)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline underline-offset-2 inline-flex items-center gap-1 hover:text-primary/80"
                >
                  {String(point.map_survey_id)}
                  <ExternalLink className="h-3 w-3 shrink-0" />
                </Link>
              </div>
            )}

            {/* Source ID (source_id1) — always visible. Always rendered as a
                link to the source's All Sources page. */}
            <div className="mt-1 flex items-center gap-1 text-xs">
              <span className="text-muted-foreground">Source ID:</span>
              {point.source_id1 == null ? (
                <span className="text-muted-foreground italic">null</span>
              ) : (
                <Link
                  to={`/all-sources/${String(point.source_id1)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline underline-offset-2 inline-flex items-center gap-1 hover:text-primary/80"
                >
                  {String(point.source_id1)}
                  <ExternalLink className="h-3 w-3 shrink-0" />
                </Link>
              )}
            </div>
          </section>
          </>
        )}

        {/* ---------------------------------------------------------------
            EXPANDED "SEE MORE" VIEW - contains the full field list
            --------------------------------------------------------------- */}
        {isExpanded && (
          <dl className="space-y-2 px-4 py-3 text-xs">
            {Object.entries(point)
              .filter(([key, value]) => key !== "validation_error" && value != null)
              .map(([key, value]) => (
                <div key={key} className="flex flex-col">
                  <dt className="text-muted-foreground font-medium">{getFieldLabel(key)}</dt>
                  <dd className={value == null ? "text-muted-foreground italic" : ""}>
                    {value == null ? (
                      "null"
                    ) : key === "source_id1" ? (
                      <Link
                        to={`/all-sources/${String(value)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary underline underline-offset-2 inline-flex items-center gap-1 hover:text-primary/80"
                      >
                        {String(value)}
                        <ExternalLink className="h-3 w-3 shrink-0" />
                      </Link>
                    ) : key === "map_survey_id" ? (
                      <Link
                        to={`/survey-catalogue/${String(value)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary underline underline-offset-2 inline-flex items-center gap-1 hover:text-primary/80"
                      >
                        {String(value)}
                        <ExternalLink className="h-3 w-3 shrink-0" />
                      </Link>
                    ) : (
                      String(value)
                    )}
                  </dd>
                </div>
              ))}
          </dl>
        )}
      </div>

      {/* Footer: "See more" toggle for the full field list */}
      <div className="border-t border-border/60 px-4 py-2 shrink-0 h-[34px]">
        <Button
          variant="ghost"
          size="sm"
          className="w-full h-5 gap-1.5 text-xs text-foreground/80"
          onClick={() => setIsExpanded((prev) => !prev)}
          aria-expanded={isExpanded}
        >
          {isExpanded ? (
            <ChevronUp className="size-3.5" aria-hidden="true" />
          ) : (
            <ChevronDown className="size-3.5" aria-hidden="true" />
          )}
          {isExpanded ? "See less" : "See more"}
        </Button>
      </div>
      </CollapsibleContent>
    </Collapsible>
  )
}
